import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stack, Typography, Grid, Alert, Box, IconButton, Snackbar, Divider, Skeleton, Button } from '@mui/material';
import api from '../Services/Api';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import Loading from '../components/Loading';
import SearchComponent from '../components/SearchComponent';
import WebIcon from '@mui/icons-material/Web';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import InboxIcon from '@mui/icons-material/Inbox';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MailOutlined from '@mui/icons-material/MailOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import MicIcon from '@mui/icons-material/Mic';
import Recorder from '../components/Recorder';
import ProcessingComponent from '../designsystem/ProcessingComponent';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    glassCard, ACCENT, ACCENT_GLOW,
    TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_HINT,
    SURFACE_BORDER, SURFACE_HOVER,
    TYPE_SCALE, noMotion
} from '../designsystem/tokens';

const Companies = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const crmUpdated = state?.ok ?? false;
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [createdate, setCreatedate] = useState("DESCENDING");

    const previousController = useRef();
    const navigate = useNavigate();

    useEffect(() => {
        api.fetchCompanies({ createdate: createdate, noCache: true }).then((response) => {
            setFilteredCompanies(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching companies", error);
            setLoading(false);
        });
    }, []);

    const handleSearchChange = (searchTerm) => {
        if (previousController.current) {
            previousController.current.abort();
        }
        const controller = new AbortController();
        const signal = controller.signal;
        previousController.current = controller;
        setLoading(true);
        api.fetchCompanies({
            value: searchTerm.toLowerCase(),
            createdate: createdate,
            after: 0,
            signal: signal
        }).then((response) => {
            setFilteredCompanies(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching companies", error);
            setLoading(false);
        });
    };

    const handleSortChange = (searchTerm, createdate) => {
        setLoading(true);
        setCreatedate(createdate);
        api.fetchCompanies({
            value: searchTerm.toLowerCase(),
            after: 0,
            createdate: createdate
        }).then((response) => {
            setFilteredCompanies(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching companies", error);
            setLoading(false);
        });
    }

    return (
        <Stack style={{ padding: '1rem', width: '100%', margin: 'auto' }}>
            <Stack direction="row" spacing={2} style={{ marginBottom: '1rem' }} alignItems="center">
                <IconButton aria-label={t('goBack', 'Go back')} style={{ color: 'white' }} onClick={() => {
                    navigate("/app");
                }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" style={{ color: 'white', textAlign: 'left', width: '100%', verticalAlign: 'middle' }} >{t('companies')}</Typography>
            </Stack>
            <Loading loading={loading} />
            <Snackbar open={crmUpdated} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert icon={<CheckIcon style={{ color: "white" }} fontSize="inherit" />} sx={{ borderRadius: '12px', backgroundColor: '#366E6B', color: 'white' }}>
                    {t('crmUpdated')}
                </Alert>
            </Snackbar>
            <SearchComponent createdate={createdate} handleSortChange={handleSortChange} handleSearchChange={handleSearchChange} />

            {!loading && filteredCompanies.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', pt: 1.5, pb: 0.5, px: 0.5 }}>
                    {filteredCompanies.length} {t('companies').toLowerCase()}
                </Typography>
            )}

            {!loading && filteredCompanies.length === 0 && (
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8, color: 'rgba(255,255,255,0.4)' }}>
                    <InboxIcon sx={{ fontSize: 48 }} />
                    <Typography variant="body1">{t('noCompaniesFound', 'No companies found')}</Typography>
                </Stack>
            )}

            <Grid data-testid="list-container" container spacing={1} style={{ marginTop: '10px' }}>
                {filteredCompanies.map((company, idx) => (
                    <Grid data-testid={`record-card-${idx}`} key={company.id || idx} item xs={12} sm={6} md={4} lg={3}>
                        <CompanyCard company={company} />
                    </Grid>
                ))}
            </Grid>

        </Stack>
    );
};

const getCompanyAvatarColor = (name) => {
    const colors = ['#76A9AC', '#AC7676', '#7676AC', '#AC9F76', '#76AC82', '#AC76A3'];
    let hash = 0;
    const str = name || '';
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const CompanyCard = ({ company }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const handleClick = () => navigate(`/app/companies/${company.id}`, { state: { company: company } });
    const handleKeyDown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } };

    const name = company.name ?? '--';
    const initial = (name.trim()[0] ?? '?').toUpperCase();
    const avatarColor = getCompanyAvatarColor(name);
    const address = (company.address || company.city || company.country) ? getAddressCompany(company, t) : null;

    return (
        <Box
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label={name}
            sx={{
                ...glassCard,
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${ACCENT_GLOW}` },
                '&:active': { transform: 'scale(0.98)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' }, '&:active': { transform: 'none' } },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    backgroundColor: avatarColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, mt: 0.25,
                }}>
                    <Typography sx={{ color: '#0a1929', fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>
                        {initial}
                    </Typography>
                </Box>

                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'white', lineHeight: 1.3 }} noWrap>
                        {name}
                    </Typography>
                    {company.domain && (
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <WebIcon sx={{ fontSize: 14, color: ACCENT, flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)' }}>
                                {company.domain}
                            </Typography>
                        </Stack>
                    )}
                    {company.phone && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhoneOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                                {company.phone}
                            </Typography>
                        </Stack>
                    )}
                    {address && (
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <PlaceOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                                {address}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}

const InfoRow = ({ icon, iconColor, label, value }) => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${iconColor || ACCENT}15`
        }}>
            {React.cloneElement(icon, { sx: { color: iconColor || ACCENT, fontSize: 22 } })}
        </Box>
        <Stack>
            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: value ? TEXT_PRIMARY : TEXT_HINT }}>
                {value || '—'}
            </Typography>
        </Stack>
    </Stack>
);

const DetailedInfoCompany = () => {
    const { t } = useTranslation();
    const { companyId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [companyData, setCompanyData] = useState(state?.company ?? null);
    const [loading, setLoading] = useState(true);
    const [showRecorder, setShowRecorder] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [configuredProps, setConfiguredProps] = useState([]);

    const fetchCompany = useCallback(() => {
        api.fetchCompany(companyId).then((response) => {
            setCompanyData(prev => prev ? { ...prev, ...response } : response);
            setLoading(false);
        }).catch(() => {
            if (!companyData) navigate('/app/companies', { replace: true });
            setLoading(false);
        });
    }, [companyId]);

    useEffect(() => {
        if (!companyId) {
            navigate('/app/companies', { replace: true });
            return;
        }
        fetchCompany();
        api.fetchProperties('company').then((props) => {
            setConfiguredProps(Array.isArray(props) ? props : []);
        }).catch(() => {});
    }, [companyId, fetchCompany]);

    const handleRecordFinished = (audio) => {
        setProcessing(true);
        const jsonObject = { company: companyData };
        api.UploadAudio(audio).then((response) => {
            setProcessing(false);
            navigate('/app/validate', { state: { text: response.text, json: jsonObject, routeBack: `/app/companies/${companyId}` } });
        }).catch((error) => {
            setProcessing(false);
            Sentry.captureException(error);
        });
    };

    if (!companyData && !loading) return null;

    // Loading skeleton
    if (!companyData && loading) {
        return (
            <Stack sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="50%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE['2xl'] }} />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="40%" sx={{ bgcolor: SURFACE_HOVER, fontSize: '1.2rem' }} />
                </Stack>
                <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER, mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER, mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: '14px', bgcolor: SURFACE_HOVER }} />
            </Stack>
        );
    }

    const name = companyData.name ?? '--';
    const initial = (name.trim()[0] ?? '?').toUpperCase();
    const avatarColor = getCompanyAvatarColor(name);
    const address = getAddressCompany(companyData, t);
    const hasAddress = companyData.address || companyData.city || companyData.country;

    const infoRows = [
        { icon: <WebIcon />, label: t('domain'), value: companyData.domain },
        { icon: <PhoneOutlined />, label: t('phone'), value: companyData.phone },
        hasAddress && { icon: <PlaceOutlined />, label: t('address'), value: address },
        { icon: <FactoryOutlined />, label: t('industry'), value: companyData.industry },
        { icon: <PeopleOutlined />, label: t('employees'), value: companyData.numberofemployees },
        { icon: <AttachMoneyIcon />, label: t('annualRevenue'), value: companyData.annualrevenue },
        { icon: <DescriptionOutlined />, label: t('description', 'Description'), value: companyData.description },
        { icon: <CalendarMonthIcon />, label: t('createdate'), value: companyData.createdate ? dayjs(companyData.createdate).format('L') : null },
    ].filter(Boolean);

    return (
        <Stack sx={{ p: { xs: 2, sm: 3 } }} spacing={2.5}>
            {/* Header */}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                    data-testid="company-detail-back"
                    aria-label={t('goBack', 'Go back')}
                    onClick={() => navigate('/app/companies')}
                    sx={{ color: TEXT_SECONDARY }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography sx={{ fontSize: TYPE_SCALE['2xl'], fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                    {name}
                </Typography>
            </Stack>

            {/* Company Avatar */}
            <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{
                    width: 56, height: 56, borderRadius: '16px',
                    backgroundColor: avatarColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{ color: '#0a1929', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1 }}>
                        {initial}
                    </Typography>
                </Box>
                {companyData.domain && (
                    <Typography sx={{ fontSize: TYPE_SCALE.md, color: TEXT_SECONDARY }}>
                        {companyData.domain}
                    </Typography>
                )}
            </Stack>

            {/* Voice Update Button */}
            <Button
                data-testid="company-voice-update"
                aria-label={t('voiceUpdate')}
                variant="outlined"
                startIcon={<MicIcon />}
                onClick={() => setShowRecorder(prev => !prev)}
                sx={{
                    borderColor: ACCENT, color: ACCENT, fontWeight: 600,
                    borderRadius: '14px', height: 48, textTransform: 'none',
                    fontSize: TYPE_SCALE.md,
                    '&:hover': { bgcolor: `${ACCENT}10`, borderColor: ACCENT },
                    ...noMotion
                }}
            >
                {t('voiceUpdate')}
            </Button>

            {/* Recorder Section */}
            {showRecorder && (
                <Box sx={{
                    ...glassCard,
                    overflow: 'hidden',
                    p: 0,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <Box
                        aria-live="polite"
                        sx={{
                            transform: 'scale(0.55)',
                            transformOrigin: 'top center',
                            mt: 7,
                            mb: -11,
                            width: '182%',
                        }}
                    >
                        {!processing && (
                            <Recorder
                                finished={handleRecordFinished}
                                onClear={() => {}}
                                onStart={() => {}}
                                small={true}
                                audioText={t('audioCompanyText')}
                            />
                        )}
                        {processing && <ProcessingComponent processingText={t('processingAudio')} />}
                    </Box>
                </Box>
            )}

            {/* Info Card */}
            <Box data-testid="company-info-card" sx={{ ...glassCard }}>
                <Stack spacing={2}>
                    {infoRows.map((row, idx) => (
                        <React.Fragment key={idx}>
                            {idx > 0 && <Divider sx={{ borderColor: SURFACE_BORDER }} />}
                            <InfoRow icon={row.icon} label={row.label} value={row.value} />
                        </React.Fragment>
                    ))}
                </Stack>
            </Box>

            {/* Associated Contacts */}
            {companyData.contacts?.length > 0 && (
                <Box sx={{ ...glassCard }}>
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('associatedContacts')}
                        </Typography>
                        {companyData.contacts.map((contact, idx) => {
                            const contactName = [contact.firstname, contact.lastname].filter(Boolean).join(' ') || '—';
                            return (
                                <Box
                                    key={contact.id || idx}
                                    data-testid={`company-contact-${idx}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${t('contact')}: ${contactName}`}
                                    onClick={() => navigate('/app/contacts/detailed', { state: { contact } })}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/contacts/detailed', { state: { contact } }); } }}
                                    sx={{
                                        ...glassCard,
                                        cursor: 'pointer',
                                        transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
                                        '&:hover': { bgcolor: SURFACE_HOVER, borderColor: `${ACCENT}30` },
                                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                                        ...noMotion
                                    }}
                                >
                                    <Stack spacing={0.5}>
                                        <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                                            {contactName}
                                        </Typography>
                                        {contact.email && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <MailOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{contact.email}</Typography>
                                            </Stack>
                                        )}
                                        {contact.phone && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PhoneOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{contact.phone}</Typography>
                                            </Stack>
                                        )}
                                        <Stack direction="row" justifyContent="flex-end">
                                            <ChevronRightIcon sx={{ color: TEXT_HINT, fontSize: 20 }} />
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}

            {/* Associated Deals */}
            {companyData.deals?.length > 0 && (
                <Box sx={{ ...glassCard }}>
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('associatedDeals')}
                        </Typography>
                        {companyData.deals.map((deal, idx) => (
                            <Box
                                key={deal.id || idx}
                                data-testid={`company-deal-${idx}`}
                                role="button"
                                tabIndex={0}
                                aria-label={`${t('deal')}: ${deal.name || '—'}`}
                                onClick={() => navigate(`/app/deals/${deal.id}`, { state: { deal } })}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/app/deals/${deal.id}`, { state: { deal } }); } }}
                                sx={{
                                    ...glassCard,
                                    cursor: 'pointer',
                                    transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
                                    '&:hover': { bgcolor: SURFACE_HOVER, borderColor: `${ACCENT}30` },
                                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                                    ...noMotion
                                }}
                            >
                                <Stack spacing={0.5}>
                                    <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                                        {deal.name || '—'}
                                    </Typography>
                                    {deal.amount != null && (
                                        <Typography sx={{ fontSize: TYPE_SCALE.sm, color: ACCENT, fontWeight: 600 }}>
                                            {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(deal.amount)}
                                        </Typography>
                                    )}
                                    {deal.stage?.label && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <WorkOutlineOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                            <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{deal.stage.label}</Typography>
                                        </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="flex-end">
                                        <ChevronRightIcon sx={{ color: TEXT_HINT, fontSize: 20 }} />
                                    </Stack>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Configured Properties */}
            {configuredProps.length > 0 && companyData.configuredProperties && (
                <Box data-testid="configured-properties-card" sx={{ ...glassCard }}>
                    <Stack spacing={2}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('additionalDetails')}
                        </Typography>
                        {configuredProps
                            .filter(prop => companyData.configuredProperties?.[prop.name] != null)
                            .map((prop, idx, arr) => (
                                <React.Fragment key={prop.name}>
                                    {idx > 0 && <Divider sx={{ borderColor: SURFACE_BORDER }} />}
                                    <Stack>
                                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {prop.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: TYPE_SCALE.md, fontWeight: 600, color: TEXT_PRIMARY }}>
                                            {formatPropertyValue(prop, companyData.configuredProperties[prop.name])}
                                        </Typography>
                                    </Stack>
                                </React.Fragment>
                            ))
                        }
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};

const formatPropertyValue = (prop, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (prop.type === 'date') return dayjs(value).format('L');
    if (prop.type === 'datetime') return dayjs(value).format('L LT');
    if (prop.type === 'enumeration' && prop.options?.length > 0) {
        const values = String(value).split(';');
        return values.map(v => prop.options.find(o => o.value === v)?.label || v).join(', ');
    }
    if (prop.type === 'number') return Number(value).toLocaleString();
    return String(value);
};

const getAddressCompany = (company, t) => {
    const address = [];
    if (company.address) address.push(company.address);
    if (company.city) address.push(company.city);
    if (company.country) address.push(company.country);
    if (address.length === 0) return t('address');
    return address.join(", ");
}

export default {
    Companies,
    CompanyCard,
    getAddressCompany,
    DetailedInfoCompany
};
